import type { KanbanColumnConfig, ProjectNode } from '@/shared/types/projectData';

export type CanonicalKanbanStatus = 'todo' | 'in-progress' | 'done';

export const KANBAN_WORKFLOW_OPTIONS: Array<{ value: CanonicalKanbanStatus; label: string }> = [
  // i18n-raw-ui-literal-exempt: Canonical persisted status display key; translated at render time.
  { value: 'todo', label: 'To Do' },
  // i18n-raw-ui-literal-exempt: Canonical persisted status display key; translated at render time.
  { value: 'in-progress', label: 'In Progress' },
  // i18n-raw-ui-literal-exempt: Canonical persisted status display key; translated at render time.
  { value: 'done', label: 'Done' },
];

export const DEFAULT_KANBAN_COLUMNS: KanbanColumnConfig[] = [
  // i18n-raw-ui-literal-exempt: Canonical persisted core-column title; translated at render time.
  { id: 'todo', title: 'To Do', icon: '📋', status: 'todo', isCore: true, isEditable: false },
  // i18n-raw-ui-literal-exempt: Canonical persisted core-column title; translated at render time.
  { id: 'in-progress', title: 'In Progress', icon: '⚡', status: 'in-progress', isCore: true, isEditable: false },
  // i18n-raw-ui-literal-exempt: Canonical persisted core-column title; translated at render time.
  { id: 'done', title: 'Done', icon: '✅', status: 'done', isCore: true, isEditable: false },
];

export function normalizeKanbanColumnId(columnId?: string | null): string {
  if (!columnId) return 'todo';
  return columnId === 'to-do' ? 'todo' : columnId;
}

export function normalizeKanbanStatus(status?: string | null): CanonicalKanbanStatus {
  switch (status) {
    case 'done':
    case 'completed':
      return 'done';
    case 'in-progress':
      return 'in-progress';
    default:
      return 'todo';
  }
}

export function isCoreKanbanColumnId(columnId?: string | null): boolean {
  const normalized = normalizeKanbanColumnId(columnId);
  return normalized === 'todo' || normalized === 'in-progress' || normalized === 'done';
}

export function getCoreKanbanColumnIdForStatus(status?: string | null): string {
  return normalizeKanbanStatus(status);
}

export function getCoreKanbanColumnTitleForStatus(status?: string | null): string {
  const normalizedStatus = normalizeKanbanStatus(status);
  return KANBAN_WORKFLOW_OPTIONS.find((option) => option.value === normalizedStatus)?.label || 'To Do';
}

/**
 * Returns the stable English message key for one of the built-in Kanban lanes.
 *
 * This deliberately identifies a lane by its persisted core id, rather than by
 * its title or status. A user-created lane may use a workflow status such as
 * `todo`, but its title is user content and must never be translated.
 */
export function getCanonicalKanbanColumnTitle(column: Pick<KanbanColumnConfig, 'id'>): string | null {
  switch (normalizeKanbanColumnId(column.id)) {
    case 'todo':
      return 'To Do';
    case 'in-progress':
      return 'In Progress';
    case 'done':
      return 'Done';
    default:
      return null;
  }
}

export function getKanbanCompletionState(status?: string | null): 'not-started' | 'in-progress' | 'completed' {
  switch (normalizeKanbanStatus(status)) {
    case 'done':
      return 'completed';
    case 'in-progress':
      return 'in-progress';
    default:
      return 'not-started';
  }
}

export function getKanbanProgressForStatus(status?: string | null, currentProgress: number = 0): number {
  switch (normalizeKanbanStatus(status)) {
    case 'done':
      return 100;
    case 'todo':
      return 0;
    default:
      return currentProgress > 0 && currentProgress < 100 ? currentProgress : 50;
  }
}

export function normalizeKanbanColumn(column: KanbanColumnConfig): KanbanColumnConfig {
  const capacity = 'maxItems' in column ? column.maxItems
    : (column as KanbanColumnConfig & { max_items?: unknown }).max_items;
  const normalizedId = normalizeKanbanColumnId(column.id);
  const rawStatus = column.status || normalizedId;
  const normalizedStatus =
    rawStatus === 'todo' ||
    rawStatus === 'done' ||
    rawStatus === 'completed' ||
    rawStatus === 'in-progress'
      ? normalizeKanbanStatus(rawStatus)
      : (isCoreKanbanColumnId(normalizedId) ? normalizeKanbanStatus(rawStatus) : 'in-progress');
  return {
    ...column,
    ...((('maxItems' in column) || ('max_items' in column)) ? {
      maxItems: typeof capacity === 'number' && Number.isSafeInteger(capacity) && capacity > 0 ? capacity : null,
    } : {}),
    id: normalizedId,
    ...('aggregateOrder' in column ? { aggregateOrder: column.aggregateOrder }
      : 'aggregate_order' in column ? { aggregateOrder: (column as KanbanColumnConfig & { aggregate_order?: number | null }).aggregate_order } : {}),
    status: normalizedStatus,
    isCore: column.isCore ?? isCoreKanbanColumnId(normalizedId),
    isEditable: column.isEditable ?? !isCoreKanbanColumnId(normalizedId),
  };
}

export function getKanbanColumnById(
  columns: KanbanColumnConfig[],
  columnId?: string | null
): KanbanColumnConfig | undefined {
  const normalizedId = normalizeKanbanColumnId(columnId);
  return columns.find((column) => normalizeKanbanColumnId(column.id) === normalizedId);
}

export function getKanbanStatusForColumn(
  columnId: string,
  columns: KanbanColumnConfig[]
): CanonicalKanbanStatus {
  const column = getKanbanColumnById(columns, columnId);
  return normalizeKanbanColumn(column || {
    id: columnId,
    title: columnId,
    icon: '',
    status: columnId,
    isEditable: true,
  }).status as CanonicalKanbanStatus;
}

export function buildKanbanViewState(
  currentViewState: ProjectNode['viewState'],
  columnId: string,
  order: number
): ProjectNode['viewState'] {
  return {
    ...currentViewState,
    kanban: {
      ...currentViewState?.kanban,
      columnId: normalizeKanbanColumnId(columnId),
      order,
    },
  };
}

export function getTaskKanbanColumnId(
  task: Pick<ProjectNode, 'status' | 'completionState' | 'viewState'>,
  columns: KanbanColumnConfig[]
): string {
  const taskStatus = normalizeKanbanStatus(task.status || task.completionState);
  const rawCurrentColumnId = task.viewState?.kanban?.columnId;
  const currentColumnId = rawCurrentColumnId ? normalizeKanbanColumnId(rawCurrentColumnId) : undefined;
  const currentColumn = currentColumnId
    ? columns.find((column) => normalizeKanbanColumnId(column.id) === currentColumnId)
    : undefined;
  if (
    currentColumnId &&
    currentColumn &&
    normalizeKanbanStatus(currentColumn.status || currentColumn.id) === taskStatus
  ) {
    return currentColumnId;
  }

  const rawStatusColumnId = task.status;
  const statusColumnId = rawStatusColumnId ? normalizeKanbanColumnId(rawStatusColumnId) : undefined;
  if (statusColumnId && columns.some((column) => normalizeKanbanColumnId(column.id) === statusColumnId)) {
    return statusColumnId;
  }

  const matchingColumn = columns.find(
    (column) => normalizeKanbanStatus(column.status || column.id) === taskStatus
  );

  return normalizeKanbanColumnId(matchingColumn?.id || getCoreKanbanColumnIdForStatus(taskStatus));
}

export function getFallbackKanbanColumnId(
  columns: KanbanColumnConfig[],
  deletedColumn?: KanbanColumnConfig | null
): string {
  const normalizedDeletedId = normalizeKanbanColumnId(deletedColumn?.id);
  const normalizedColumns = columns.map(normalizeKanbanColumn);
  const targetStatus = normalizeKanbanStatus(deletedColumn?.status || deletedColumn?.id);

  const sameStatusCoreColumn = normalizedColumns.find(
    (column) =>
      normalizeKanbanColumnId(column.id) !== normalizedDeletedId &&
      !!column.isCore &&
      normalizeKanbanStatus(column.status || column.id) === targetStatus
  );
  if (sameStatusCoreColumn) {
    return sameStatusCoreColumn.id;
  }

  const todoCoreColumn = normalizedColumns.find(
    (column) => normalizeKanbanColumnId(column.id) !== normalizedDeletedId && column.id === 'todo'
  );
  if (todoCoreColumn) {
    return todoCoreColumn.id;
  }

  const firstAvailableColumn = normalizedColumns.find(
    (column) => normalizeKanbanColumnId(column.id) !== normalizedDeletedId
  );
  return firstAvailableColumn?.id || 'todo';
}
